import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { Box, CountryFlag, TCountryCode } from "@govlaunch/web";
import GoogleMapReact from "google-map-react";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { Hover } from "react-powerplug";
import ToggleFollow from "~/lib/mutations/ToggleFollow";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import { ISelfieQuery } from "~/lib/queries/__generated__/SelfieQuery.generated";
import getCityType from "~/lib/utils/getCityType";
import { IGovernment } from "~/types/types";
import Geocode from "~/components/Geocode";
import { Margin } from "~/components/spacings";
import { getFormattedBudget } from "~/components/government/GovernmentBudget";
import GovernmentGroups from "~/components/government/GovernmentGroups";
import GovernmentPopulation from "~/components/government/GovernmentPopulation";
import GovernmentWebsite from "~/components/government/GovernmentWebsite";
import SharingSettingsModal from "~/components/government/SharingSettingsModal";

type Content = "connect" | "projects" | "products" | "stories" | "sharing" | "settings" | "actions";

interface IFollowButton {
  government: Pick<IGovernment, "_id" | "isFollowing">;
}

interface IGovernmentInfo {
  government: Pick<
    IGovernment,
    | "_id"
    | "slug"
    | "name"
    | "isFollowing"
    | "logo"
    | "budget"
    | "currency"
    | "viewerIsMember"
    | "isInnovator"
    | "enableSharing"
    | "sharing"
    | "site"
  > & {
    city: NonNullable<IGovernmentQuery["government"]>["city"];
    groups: NonNullable<IGovernmentQuery["government"]>["groups"];
  };
  content?: Content;
  user: ISelfieQuery["selfie"];
}

export default function GovernmentInfo({ government, content, user }: IGovernmentInfo): ReactElement {
  const router = useRouter();
  return (
    <div
      data-intercom-target={government.viewerIsMember ? "Government Details" : null}
      css={{
        backgroundColor: palette.white,
        border: `solid 1px ${palette.lightestGray}`,
        borderRadius: 5,
        position: "relative",
      }}
    >
      <SharingSettingsModal
        government={government}
        isOpen={content === "sharing"}
        onRequestClose={() => {
          return router.push(
            "/governments/[governmentSlug]/[[...content]]",
            `/governments/${government.slug}/settings`,
          );
        }}
      />

      <div
        css={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          padding: 16,
        }}
      >
        <Link
          href="/governments/[governmentSlug]/[[...content]]"
          as={`/governments/${government.slug}`}
          passHref={true}
        >
          <a
            css={{
              color: "unset",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ProfilePicture
              size={80}
              name={government.name}
              image={government.logo}
              innovator={government.isInnovator}
            />
          </a>
        </Link>

        <div>
          <h1
            css={{
              fontSize: 24,
              color: palette.text,
              fontWeight: "bold",
              lineHeight: 1.44,
              margin: 0,
              textAlign: "center",
            }}
          >
            {government.name}
          </h1>

          <p
            css={{
              fontSize: 14,
              color: palette.mediumGray,
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            <FormattedMessage
              defaultMessage="{cityType} in {stateName}"
              id="wh7SV3"
              values={{
                cityType: getCityType(government.city.governmentType || ""),
                stateName: government.city.state,
              }}
            />
          </p>

          <Box display="flex" alignItems="center" justifyContent="center" py={2}>
            <CountryFlag countryCode={government.city.country as TCountryCode} width={24} height={14} />
            <p
              css={{
                fontSize: 14,
                marginLeft: 8,
                color: palette.mediumGray,
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {government.city.countryName}
            </p>
          </Box>

          {Number(government.budget) > 0 ? (
            <span
              css={{
                fontSize: 12,
                color: palette.sealBlue,
                fontWeight: 500,
                display: "flex",
                marginTop: 4,
                justifyContent: "center",
              }}
            >
              <GovernmentPopulation population={government.city.population || 0} />
              &nbsp;
              <FormattedMessage id="Hmd2rc" defaultMessage="population" />
              &nbsp;• {getFormattedBudget(government.budget || 0, government.currency)}{" "}
              <FormattedMessage defaultMessage="budget" id="iBmHR/" />
            </span>
          ) : (
            <span
              css={{
                fontSize: 12,
                color: palette.sealBlue,
                fontWeight: 500,
                display: "flex",
                marginTop: 4,
                justifyContent: "center",
              }}
            >
              <GovernmentPopulation population={government.city.population || 0} />
              &nbsp;
              <FormattedMessage id="Hmd2rc" defaultMessage="population" />
            </span>
          )}
        </div>
      </div>

      {user && user.canSeeActiveGovernments && !government.viewerIsMember && (
        <Margin mb={16}>
          <Margin mb={16}>
            <div
              css={{
                height: 1,
                width: "100%",
                backgroundColor: palette.lightestSealBlue,
              }}
            />
          </Margin>

          <div
            css={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <FollowButton government={government} />
          </div>
        </Margin>
      )}

      <Geocode
        location={
          government.city.zip || government.city.county
            ? [government.city.name, government.city.county, government.city.zip].filter(Boolean).join(" ")
            : government.city.name
        }
      >
        {({ coordinates, loading }) => {
          if (loading || !coordinates) {
            return null;
          }

          return (
            <div
              css={{
                width: "100%",
                height: 270,
              }}
            >
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: process.env.GOOGLE_MAPS_API_KEY || "",
                }}
                defaultCenter={coordinates}
                defaultZoom={12}
                options={{
                  styles: [
                    {
                      featureType: "all",
                      elementType: "all",
                      stylers: [
                        {
                          hue: "#e7ecf0",
                        },
                      ],
                    },
                    {
                      featureType: "poi",
                      elementType: "all",
                      stylers: [
                        {
                          visibility: "off",
                        },
                      ],
                    },
                    {
                      featureType: "road",
                      elementType: "all",
                      stylers: [
                        {
                          saturation: -70,
                        },
                      ],
                    },
                    {
                      featureType: "transit",
                      elementType: "all",
                      stylers: [
                        {
                          visibility: "off",
                        },
                      ],
                    },
                    {
                      featureType: "water",
                      elementType: "all",
                      stylers: [
                        {
                          visibility: "simplified",
                        },
                        {
                          saturation: -60,
                        },
                      ],
                    },
                  ],
                }}
              />
            </div>
          );
        }}
      </Geocode>

      {government.site && (
        <div css={{ padding: 16 }}>
          <GovernmentWebsite url={government.site} />
        </div>
      )}

      {government.viewerIsMember && government.groups.length > 0 && <GovernmentGroups government={government} />}
    </div>
  );
}

const FollowButton: React.FunctionComponent<IFollowButton> = ({ government }) => {
  return (
    <Hover>
      {({ hovered, bind }) => (
        <div
          css={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ToggleFollow
            targetId={government._id}
            entityType="Government"
            viewerIsFollowing={government.isFollowing as boolean}
          >
            {({ toggle }) => {
              if (!government.isFollowing) {
                return (
                  <Button theme="primary" size="xsmall" onClick={toggle} {...bind}>
                    <FormattedMessage defaultMessage="Follow" id="ieGrWo" />
                  </Button>
                );
              }

              if (!hovered) {
                return (
                  <Button theme="primary" size="xsmall" onClick={toggle} {...bind}>
                    <FormattedMessage defaultMessage="Following" id="cPIKU2" />
                  </Button>
                );
              }

              return (
                <Button
                  theme="primary"
                  color={palette.red}
                  textColor={palette.white}
                  size="xsmall"
                  onClick={toggle}
                  {...bind}
                >
                  <FormattedMessage defaultMessage="Unfollow" id="izWS4J" />
                </Button>
              );
            }}
          </ToggleFollow>
        </div>
      )}
    </Hover>
  );
};
